/**
 * Onboarding's rules as plain functions — w12b, T-1.22 (C11, FR-A9).
 *
 * The order of the steps is the service's (`steps` on every read, interface
 * I-2) and which of them may be skipped is the service's too (`optional`).
 * What lives here is only what the *screen* decides: the words for each step,
 * which fields a step sends, where a firm stopped at an old step name lands,
 * how far along the progress bar is, and which blanks the platform's
 * organisation may fill.
 */

import type { FirmProfile, FirmSettings, OnboardingState, OrgSuggestion } from '../../../lib/api/firm'

/** I-2, for the moment before the first read has answered. */
export const ONBOARDING_STEPS = [
  'profession',
  'firm',
  'address',
  'billing',
  'website',
  'domain',
  'done',
] as const

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

/** The service's read, with the w12b field `optional` (which steps show
 *  *Skip for now*). Absent from an older service: then the fallback below. */
export type OnboardingRead = OnboardingState & { optional?: string[] }

/** The steps with no requirement — used only when the service did not say. */
export const FALLBACK_OPTIONAL = ['address', 'billing', 'domain']

/** FR-A9: *"a firm stopped at an old step name (`details`, `template`)
 *  resumes at its new equivalent."* The service maps them too; this keeps a
 *  screen that meets one (an old cached read) from drawing nothing. */
export const LEGACY_STEPS: Record<string, OnboardingStep> = {
  details: 'firm',
  template: 'website',
}

export function resumeStep(step: string | null | undefined): OnboardingStep {
  const name = LEGACY_STEPS[step ?? ''] ?? step
  return (ONBOARDING_STEPS as readonly string[]).includes(name ?? '')
    ? (name as OnboardingStep)
    : 'profession'
}

/** One line per step for the stepper — what the step is *for*. */
export const STEP_LINES: Record<OnboardingStep, string> = {
  profession: 'What your practice does',
  firm: 'Names, contact and registrations',
  address: 'Where the office is, and when it opens',
  billing: 'How your invoices are numbered',
  website: 'The look of your website',
  domain: 'Where your website will live',
  done: 'What to do next',
}

/** The profile fields each step sends — only these, so a step never re-sends
 *  (and can never be refused for) an answer that belongs to another. */
export const STEP_FIELDS: Record<OnboardingStep, Array<keyof FirmProfile>> = {
  // w13b — *"What do you do?"* rides with the profession (ADR-0031 §1).
  profession: ['profession', 'profession_label'],
  firm: ['legal_name', 'display_name', 'email', 'mobile', 'pan', 'gstin', 'tan'],
  address: [
    'address_line1',
    'address_line2',
    'city',
    'state',
    'pincode',
    'landline',
    'working_hours',
  ],
  billing: [],
  website: ['website_template'],
  // C27 (UAT report #7), ADR-0051 §1: **nothing**. The hostname is adopted
  // from core's bound list by the domain card (`POST /domain/adopt`), never
  // typed, and `POST /onboarding` now refuses a `custom_domain` as
  // `PATCH /profile` does.
  domain: [],
  done: [],
}

/** The settings the billing step sends. */
export const BILLING_FIELDS: Array<keyof FirmSettings> = [
  'financial_year_start_month',
  'default_place_of_supply',
  'invoice_series_prefix',
]

/** The next step after `step` — `done` is reached only by *Finish*. */
export function nextStep(step: OnboardingStep): OnboardingStep {
  const index = ONBOARDING_STEPS.indexOf(step)
  return ONBOARDING_STEPS[Math.min(index + 1, ONBOARDING_STEPS.length - 1)]!
}

export function previousStep(step: OnboardingStep): OnboardingStep | null {
  const index = ONBOARDING_STEPS.indexOf(step)
  return index > 0 ? ONBOARDING_STEPS[index - 1]! : null
}

/** *"Step 3 of 7"* and the bar under it — the phone's stepper. */
export function progress(step: OnboardingStep): { number: number; total: number; percent: number } {
  const total = ONBOARDING_STEPS.length
  const number = ONBOARDING_STEPS.indexOf(step) + 1
  return { number, total, percent: Math.round((number / total) * 100) }
}

/** Where a step sits for the stepper: done (a tick), current, or ahead. A step
 *  is done when it is behind the furthest step the service says the firm has
 *  reached — or everything is, once the firm has finished. */
export function stepStatus(
  step: OnboardingStep,
  viewing: OnboardingStep,
  furthest: OnboardingStep,
): 'done' | 'current' | 'ahead' {
  if (step === viewing) return 'current'
  const at = ONBOARDING_STEPS.indexOf(step)
  if (furthest === 'done' || at < ONBOARDING_STEPS.indexOf(furthest)) return 'done'
  return 'ahead'
}

/** A firm may open any step it has reached, and none beyond. */
export function canOpen(step: OnboardingStep, furthest: OnboardingStep): boolean {
  if (step === 'done') return furthest === 'done'
  return ONBOARDING_STEPS.indexOf(step) <= ONBOARDING_STEPS.indexOf(furthest)
}

/**
 * ADR-0029 in onboarding: the platform's organisation fills **blanks only**.
 * A field the firm has already answered is never replaced here — the *Import
 * from organisation* dialog on *Your firm* is where a difference is offered.
 * Returns the draft with the blanks filled, and the fields it filled.
 */
export function fillBlanks(
  draft: Partial<FirmProfile>,
  suggestions: OrgSuggestion[],
  fields: Array<keyof FirmProfile>,
): { draft: Partial<FirmProfile>; filled: Array<keyof FirmProfile> } {
  const next: Record<string, unknown> = { ...draft }
  const filled: Array<keyof FirmProfile> = []
  for (const row of suggestions) {
    if (!fields.includes(row.field)) continue
    const current = draft[row.field]
    const blank = current === null || current === undefined || String(current).trim() === ''
    if (!blank || !row.suggested) continue
    next[row.field] = row.suggested
    filled.push(row.field)
  }
  return { draft: next as Partial<FirmProfile>, filled }
}

/** What a required step still wants, before the request — the service's
 *  requirement, mirrored so *Continue* can say it instead of failing. */
export function stepGaps(step: OnboardingStep, draft: Partial<FirmProfile>): string[] {
  const blank = (value: unknown) => !value || String(value).trim() === ''
  if (step === 'profession' && blank(draft.profession)) return ['Choose your profession']
  if (step === 'profession' && draft.profession === 'other' && blank(draft.profession_label))
    return ['Tell us what your firm does']
  if (step === 'firm') {
    const gaps: string[] = []
    if (blank(draft.legal_name)) gaps.push('the firm’s legal name')
    if (blank(draft.email)) gaps.push('an email address')
    return gaps
  }
  if (step === 'website' && blank(draft.website_template)) return ['Choose a template']
  return []
}

/** The three next things a new firm does (FR-A9's *Done*), each a link. */
export const NEXT_STEPS: Array<{ key: string; title: string; body: string; href: string }> = [
  {
    key: 'client',
    title: 'Add your first client',
    body: 'Clients carry their cases, documents and invoices. Import a list or add one by hand.',
    href: '/clients',
  },
  {
    key: 'services',
    title: 'Review your services',
    body: 'The services you offer, with the workflow and compliance dates each one follows.',
    href: '/services',
  },
  {
    key: 'website',
    title: 'Make your website yours',
    body: 'Your pages are drafted from your profession. Add your words, then publish.',
    href: '/site',
  },
]

/**
 * *Done*'s three next steps for this firm. w13b — ADR-0031 §5: nothing is
 * shipped as services for `other`, so its second step says *add your own*
 * rather than pointing at a starting catalog that does not exist for it.
 */
export function nextStepsFor(profession: string | null | undefined): typeof NEXT_STEPS {
  if (profession !== 'other') return NEXT_STEPS
  return NEXT_STEPS.map((step) =>
    step.key === 'services'
      ? {
          ...step,
          title: 'Add your own services',
          body: 'ProDesk ships no starting list for your kind of practice. Add what you offer — each service with its own workflow.',
        }
      : step,
  )
}
